import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap, timeout } from 'rxjs/operators';
import { APP_PATHS, XEMEX_OID } from '@core/constants';
import { getUserInitialPartner } from '@core/models';
import { AuthStore } from '../store/auth.store';
import { UserStore } from '../store/user.store';
import { resolvePostLoginRedirectTree } from './auth-redirect.util';

/** Fail-closed ceiling so a stuck profile fetch can't hang navigation to a Xemex-only page. */
const XEMEX_GUARD_TIMEOUT_MS = 15_000;

/**
 * Protects the authenticated app shell. Waits until auth state is resolved
 * (authReady) so the login page can never flash, then:
 *  - no token        -> redirect to /auth/login (carrying the attempted URL),
 *  - valid token     -> allow,
 *  - expired token   -> refresh first (SSO via MSAL silent, password via the token
 *                       endpoint), allow on success, redirect to login on failure.
 *
 * The expired branch closes a gap where the guard trusted token PRESENCE only: an
 * expired token used to open the shell, and the refresh/redirect happened reactively
 * on the first API call (or not at all if none fired).
 */
export const authGuard: CanActivateFn = (_route, state: RouterStateSnapshot) => {
  const authStore = inject(AuthStore);
  const router = inject(Router);
  const loginTree = () =>
    router.createUrlTree(['/', APP_PATHS.Auth, APP_PATHS.Login], {
      queryParams: { redirect_uri: state.url },
    });
  return toObservable(authStore.authReady).pipe(
    filter(ready => ready),
    take(1),
    switchMap(() => {
      if (!authStore.isAuthenticated()) return of(loginTree());
      if (!authStore.isAccessTokenExpired()) return of(true);
      return authStore.ensureFreshToken().pipe(
        map(() => true),
        catchError(() => of(loginTree())),
      );
    }),
  );
};

/**
 * Owns the post-SSO landing. MSAL is configured with navigateToLoginRequestUrl=false,
 * so after the Azure redirect it parks the app on /auth-callback/ and hands navigation
 * to Angular. This guard waits for auth to settle (authReady, flipped by AppComponent on
 * InteractionStatus.None or by loggedInViaMSAL), then resolves the stored deep-link
 * (redirect_uri) as a SINGLE navigation off the callback route.
 *
 * Doing it here — instead of an imperative navigateByUrl in loggedInViaMSAL — makes the
 * callback route the one and only authority for where SSO lands, so the deep link can no
 * longer be clobbered by the default-route navigation (the bug), and it works even on a
 * silent ACQUIRE_TOKEN_SUCCESS re-auth that never emits LOGIN_SUCCESS.
 */
export const authCallbackGuard: CanActivateFn = () => {
  const authStore = inject(AuthStore);
  const router = inject(Router);
  return toObservable(authStore.authReady).pipe(
    filter(ready => ready),
    take(1),
    map(() => {
      if (!authStore.isAuthenticated()) {
        // MSAL settled without a session (e.g. error response) -> back to login.
        return router.createUrlTree(['/', APP_PATHS.Auth, APP_PATHS.Login]);
      }
      const redirectUrl = globalThis.sessionStorage.getItem('redirect_uri');
      globalThis.sessionStorage.removeItem('redirect_uri');
      return resolvePostLoginRedirectTree(router, redirectUrl);
    }),
  );
};

/** Bounces already-authenticated users away from the login route. */
export const loginGuard: CanActivateFn = () => {
  const authStore = inject(AuthStore);
  const router = inject(Router);
  return toObservable(authStore.authReady).pipe(
    filter(ready => ready),
    take(1),
    map(() => (authStore.isAuthenticated() ? router.createUrlTree(['/']) : true)),
  );
};

/**
 * Restricts a route to Xemex (internal) users — those whose initial partner is the
 * Xemex organization (XEMEX_OID). Mirrors the `xemexOnly` nav gating in the sidebar so
 * the page can't be reached by typing the URL directly.
 *
 * The authenticated shell (PagesComponent) is what normally loads the profile, but
 * CanActivate guards resolve BEFORE the shell is created, so on a direct navigation the
 * partners list is still empty here. The guard therefore kicks off the profile load
 * itself and waits for partners to arrive, failing closed (redirect to the default page)
 * on timeout or error. Both toObservable() calls are created up front, in the guard's
 * injection context, so the async pipeline never touches inject() out of context.
 */
export const xemexGuard: CanActivateFn = () => {
  const authStore = inject(AuthStore);
  const userStore = inject(UserStore);
  const router = inject(Router);
  const authReady$ = toObservable(authStore.authReady);
  const user$ = toObservable(userStore.user);
  const denyTree = () => router.createUrlTree(['/', APP_PATHS.Devices]);
  const isXemex = () => getUserInitialPartner(userStore.user())?.partnerOid === XEMEX_OID;

  return authReady$.pipe(
    filter(ready => ready),
    take(1),
    tap(() => {
      // Direct navigation: the shell hasn't loaded the profile yet, so trigger it here.
      if (userStore.user().partners.length === 0 && authStore.oid()) {
        userStore.getUserMethod(authStore.oid());
      }
    }),
    switchMap(() =>
      user$.pipe(
        filter(user => user.partners.length > 0),
        take(1),
        timeout(XEMEX_GUARD_TIMEOUT_MS),
        map(() => (isXemex() ? true : denyTree())),
        catchError(() => of(denyTree())),
      ),
    ),
  );
};
